import { formatDate } from '@angular/common';
import { Component, OnInit, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SelectItem } from 'primeng/api';
import { tap } from 'rxjs';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { DropdownFilterService } from '../../../../core/services/dropdown-filter.service';
import { State } from '../../../../core/store';
import { awardQuarterlyBonus, awardQuarterlyBonusSuccess, getCompletedKraList, recalculateQuarterlyBonus, recalculateQuarterlyBonusSuccess, rejectQuarterlyBonus, rejectQuarterlyBonusSuccess, resetQuarterlyBonus, resetQuarterlyBonusSuccess, saveEmployeeNoteSuccess, saveMeetsExpectations, saveMeetsExpectationsSuccess, showEditEmployeeNote, unlockEmployeeReviewOversight, unlockEmployeeReviewOversightSuccess } from '../../../../core/store/employee-review/employee-review.actions';
import { loadEmployeeUsers, loadViewableCampuses } from '../../../../core/store/employee/employee.action';
import { deepClone, getDateWithOffset } from '../../../../helpers/utils';
import { DateRangeModel } from '../../../../models/dateRangeModel';
import { CompletedKraModel, OversightReviewQuarterlyBonusModel } from '../../../../models/employeeReviewModels';
import { PagingResultsModel } from '../../../../models/pagingResultsModel';
import { UserAutoCompleteItem } from '../../../../models/utilModels';

@Component({
    selector: 'app-report-completed-kra',
    templateUrl: './report-completed-kra.component.html',
    styleUrls: ['./report-completed-kra.component.scss'],
    standalone: false
})
export class ReportCompletedKraComponent extends BaseComponent implements OnInit {
  list: PagingResultsModel<CompletedKraModel>;
  filter: any;
  loadingList: boolean = true;
  saving: boolean = false;
  hasProfitSharingFeatureSetting: boolean;

  campusList: SelectItem[];
  campusId: number = 0;

  selectedUserEmployee: UserAutoCompleteItem;
  userEmployeeList: UserAutoCompleteItem[];
  userId: number = 0;

  startDate: string = formatDate(getDateWithOffset(-90), 'yyyy-MM-dd', 'en-US');
  endDate: string = formatDate(new Date(), 'yyyy-MM-dd', 'en-US');

  meetsRequirements: string = "null";
  statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Offer letter sent', value: 'true' },
    { label: 'Does not meet expectations', value: 'false' },
    { label: 'Not assigned', value: 'null' },
  ];

  yesNoAllOptions = [
    { label: 'All', value: null },
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  terminatedEmployeesFilter?: boolean = false;
  inactiveUsersFilter?: boolean = false;

  canViewKraReport;
  canViewHrNotes;
  canEditHrNotes;

  totalRecords = signal<number>(0);
  dropdownOptions = computed(() => {
    var total = this.totalRecords();
    return [
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
      { label: '200', value: 200 },
      { label: '500', value: 500 },
      { label: 'All', value: total > 0 ? total : 20 } 
    ];
  });

  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store<State>,
    private dropdownFilterService: DropdownFilterService  ) {
    super();
  }

  ngOnInit() {
    this.subs.push(
      this.store.select(s => s.employeeState.campuses).subscribe(campuses => {
        this.campusList = campuses && campuses.length > 0 ? campuses
          : [{ label: 'loading...', value: undefined }];
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userEmployeeList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.store.select(s => s.employeeReviewState.loadingCompletedKraList).subscribe(loadingCompletedKraList => {
        this.loadingList = loadingCompletedKraList;
      }),
      this.store.select(s => s.employeeReviewState.savingMeetsExpectations).subscribe(saving => {
        this.saving = saving;
      }),
      this.store.select(s => s.employeeReviewState.completedKraList).subscribe(completedKraList => {
        this.list = deepClone(completedKraList);
  			if(completedKraList != null)
     	  	  	  this.totalRecords.set(completedKraList.totalRecords);        
      }),
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canViewKraReport = permissionIDs && !!permissionIDs.find(p => p === 3208); //Can view KRAs
        this.canViewHrNotes = permissionIDs && !!permissionIDs.find(p => p === 3221 || p === 3222); //Can view or manage Hr Notes
        this.canEditHrNotes = permissionIDs && !!permissionIDs.find(p => p === 3222);
      }),
      this.store.select(s => s.accountState.featureSettingsIds).subscribe(featureSettingsIds => {
        this.hasProfitSharingFeatureSetting = featureSettingsIds && (!!featureSettingsIds.find(p => p === 3005)); //Profit Sharing
      }),
      this.actions$.pipe(
        ofType(saveMeetsExpectationsSuccess),
        tap(action => {
          this.reloadList();
        })
      ).subscribe(),
      this.actions$.pipe(
        ofType(awardQuarterlyBonusSuccess),
        tap(action => {
          this.reloadList();
        })
      ).subscribe(),
      this.actions$.pipe(
        ofType(recalculateQuarterlyBonusSuccess),
        tap(action => {
          this.reloadList();
        })
      ).subscribe(),
      this.actions$.pipe(
        ofType(rejectQuarterlyBonusSuccess),
        tap(action => {
          this.reloadList();
        })
      ).subscribe(),
      this.actions$.pipe(
        ofType(resetQuarterlyBonusSuccess),
        tap(action => {
          this.reloadList();
        })
      ).subscribe(),
      this.actions$.pipe(
        ofType(saveEmployeeNoteSuccess),
        tap(action => {
          this.reloadList();
        })
      ).subscribe(),
      this.actions$.pipe(
        ofType(unlockEmployeeReviewOversightSuccess),
        tap(action => {
          this.reloadList();
        })
      ).subscribe()
    );

    this.store.dispatch(loadViewableCampuses());    
  }

  reloadFilter(event) {
    this.filter = event;
    this.reloadList();
  }

  reloadList() {
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.startDate = { Name: 'startDate', Value: { value: this.startDate } };
    this.filter.filters.endDate = { Name: 'endDate', Value: { value: this.endDate } };
    this.filter.filters.meetsRequirements = { Name: 'meetsRequirements', Value: { value: this.meetsRequirements } };
    this.filter.filters.showTerminatedEmployees = { Name: 'showTerminatedEmployees', Value: { value: this.terminatedEmployeesFilter } };
    this.filter.filters.showInactiveUsers = { Name: 'showInactiveUsers', Value: { value: this.inactiveUsersFilter } };

    this.store.dispatch(getCompletedKraList({
      filter: this.filter
    }));
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.campusId,
      payrollClassificationId: null,
      showInactive: this.inactiveUsersFilter !== false ? true : false,
      showTerminated: this.terminatedEmployeesFilter !== false ? true : false
    }));
  }

  reloadListAfterStaffChage(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.userId = selectedUser.value.id;
    this.reloadList();
  }

  reloadListAfterClear(event) {    
    this.userId = 0;
    this.reloadList();
  }

  openReview(review) {
      this.openLink(`/employee-review/oversight/view/${review.oversightReviewID}`);    
  }

  openLink(path) {
    this.router.navigate([path]);
  }

  unlockOversightReview(review) {
    this.store.dispatch(unlockEmployeeReviewOversight({ reviewId: review.oversightReviewID }));
  }

  saveOfferLetterSent(review: CompletedKraModel) {
    let rev = deepClone(review) as CompletedKraModel;
    rev.meetsNewOfferLetterRequirements = true;
    this.store.dispatch(saveMeetsExpectations({ review: rev }));
  }

  saveDoesNotMeetExpections(review: CompletedKraModel) {
    let rev = deepClone(review) as CompletedKraModel;
    rev.meetsNewOfferLetterRequirements = false;
    this.store.dispatch(saveMeetsExpectations({ review: rev }));
  }

  saveReset(review: CompletedKraModel) {
    let rev = deepClone(review) as CompletedKraModel;
    rev.meetsNewOfferLetterRequirements = null;
    this.store.dispatch(saveMeetsExpectations({ review: rev }));
  }

  openOfferLetter(review: CompletedKraModel) {
    if (review.offerLetterId > 0) {
      window.open(`/offerletter/create/${review.offerLetterId}`, "_blank");
    }
  }

  addQuarterlyBonus(review: CompletedKraModel) {
    this.store.dispatch(awardQuarterlyBonus({ oversightReviewId: review.oversightReviewID }));
  }

  redoQuarterlyBonus(bonus: OversightReviewQuarterlyBonusModel) {
    this.store.dispatch(recalculateQuarterlyBonus({ payrollBonusId: bonus.payrollBonusId }));
  }

  toggleRejectionReason(review: CompletedKraModel) {
    review.showRejectedReason = !review.showRejectedReason;
  }

  rejectQuarterlyBonus(review: CompletedKraModel) {
    this.store.dispatch(rejectQuarterlyBonus({ oversightReviewId: review.oversightReviewID, reason: review.quarterlyBonusRejectedReason }));
  }

  resetQuarterlyBonus(review: CompletedKraModel) {
    this.store.dispatch(resetQuarterlyBonus({ oversightReviewId: review.oversightReviewID }));
  }

  addHrNote(review: CompletedKraModel) {
    this.store.dispatch(showEditEmployeeNote({ employeeId: review.employeeId }));
  }

  getTerminationString(review) {
    if (review.terminationDate != null) {
      return "(Separated)";
    }

    if (review.inactive) {
      return "(Inactive)"
    }

    return "";
  }

  truncateHtmlString(htmlString) {
    if (htmlString == null) {
      return "";
    }

    var maxLength = 150;
    if (htmlString.length <= maxLength) {
      return htmlString;
    }

    maxLength = htmlString.substring(0, maxLength).lastIndexOf(' ');
    return htmlString.substring(0, maxLength) + "...";
  }

  getTitleString(htmlString) {
    if (htmlString == null) {
      return "";
    }

    var strippedHtml = htmlString.replace(/<\/p>/g, '<br />').replace(/<br\s*[\/]?>/gi, "\n").replace(/<[^>]+>/g, '');
    return strippedHtml;
  }
}
